import type { ExteriorMaterial } from "building-types/materials/exteriorMaterialTypes";
import type { GeneratedRoof, GeneratedRoofSystem } from "building-types/roofs/generatedRoofSystemTypes";
import { RIDGE_RAIL_TOLERANCE, VALLEY_RAIL_TOLERANCE } from "building-types/roofs/roofConstants";
import {
  DEFAULT_ROOFING_MATERIAL_EXTENSION,
  DEFAULT_ROOFING_MATERIAL_THICKNESS,
} from "building-types/roofs/roofConstants";
import type { RoofSettings } from "building-types/settings/roofSettings";
import { assertIsNotNull } from "common/utils/assertOfType";
import { aabb3Overlaps } from "geom/aabb3";
import { faceOrNullCopy, faceOrNullMoveAlongNormal, type Face } from "geom/brep";
import { DIST_EPSILON } from "geom/core";
import { intersectFaceFace } from "geom/intersect";
import { IntersectionKind } from "geom/intersectRay2";
import { joinPolyLine3Rails } from "geom/join";
import { planeNormal, planeIsParallel } from "geom/plane";
import { polyLine3ByTwoPoints, polyLine3Length, polyLine3Move, polyLine3Reverse, type PolyLine3 } from "geom/polyLine3";
import { vec3AngleBetween } from "geom/vec3";

export function getRoofingMaterialThickness(settings: RoofSettings): number {
  return settings.roofingMaterialThickness ?? DEFAULT_ROOFING_MATERIAL_THICKNESS;
}

export function getRoofingMaterial(settings: RoofSettings): ExteriorMaterial {
  return settings.roofingMaterial ?? "roof0";
}

export function getRoofingMaterialExtension(settings: RoofSettings): number {
  return settings.roofingMaterialExtension ?? DEFAULT_ROOFING_MATERIAL_EXTENSION;
}

export function getRoofFaces(roof: GeneratedRoof): Face[] {
  return roof.roofTopFaces.map((x) => x.face);
}

export function getRoofRidges(
  roof: GeneratedRoofSystem,
  isHorizontal: boolean,
  filter?: (entity: GeneratedRoofSystem | GeneratedRoof) => boolean,
): PolyLine3[] {
  const finalRidgeRails: PolyLine3[] = [];

  const { roofs: subRoofs } = roof;
  if (!subRoofs) {
    return finalRidgeRails;
  }
  const allSubRoofs = Object.values(subRoofs);

  const ridgeRails: PolyLine3[] = [];
  for (let i = 0, l = allSubRoofs.length; i < l; i++) {
    const subRoof = allSubRoofs[i];
    if (filter && !filter(subRoof)) {
      continue;
    }
    subRoof.rails.ridgeRails
      .filter((x) => x.isHorizontal === isHorizontal)
      .forEach((x) => ridgeRails.push(...x.clippedRails));
  }

  finalRidgeRails.push(
    ...joinPolyLine3Rails({
      segments: ridgeRails,
      searchBoxTolerance: RIDGE_RAIL_TOLERANCE,
      simplify: true,
      allowRevert: false,
    }),
  );

  return finalRidgeRails;
}

/**
 * @NOTE(Andrew): See TODO below: collectSubRoofValleys is extremely slow, so this
 * function should not be used during building generation.
 */
export function getRoofValleys(
  roof: GeneratedRoofSystem,
  filter?: (entity: GeneratedRoofSystem | GeneratedRoof) => boolean,
): PolyLine3[] {
  const finalValleyRails: PolyLine3[] = [];

  const { roofs: subRoofs } = roof;
  const allSubRoofs = Object.values(subRoofs);

  const valleyRails: PolyLine3[] = [];
  const horizontalValleyRails: PolyLine3[] = [];
  for (let i = 0, l = allSubRoofs.length; i < l; i++) {
    const subRoof = allSubRoofs[i];
    if (filter && !filter(subRoof)) {
      continue;
    }
    collectSubRoofValleys(i, subRoof, allSubRoofs, valleyRails, horizontalValleyRails, filter);
  }

  finalValleyRails.push(
    ...joinPolyLine3Rails({
      segments: valleyRails,
      searchBoxTolerance: VALLEY_RAIL_TOLERANCE,
      simplify: true,
      allowRevert: false,
    }),
  );

  finalValleyRails.push(
    ...joinPolyLine3Rails({
      segments: horizontalValleyRails,
      searchBoxTolerance: VALLEY_RAIL_TOLERANCE,
      simplify: true,
    }),
  );

  return finalValleyRails;
}

/**
 * @TODO(Andrew): This is extremely slow and must be optimized before it can be used
 * during building generation.
 */
export function collectSubRoofValleys(
  id: number,
  subRoof: GeneratedRoof,
  subRoofs: GeneratedRoof[],
  valleyRails: PolyLine3[],
  horizontalValleyRails: PolyLine3[],
  filter?: (entity: GeneratedRoofSystem | GeneratedRoof) => boolean,
) {
  const { roofVolume, roofTopFaces } = subRoof;
  const { aabb3 } = roofVolume;
  const offset = VALLEY_RAIL_TOLERANCE * 0.5;

  for (let otherId = 0, subRoofCount = subRoofs.length; otherId < subRoofCount; otherId++) {
    if (otherId <= id) {
      continue;
    }

    const otherSubRoof = subRoofs[otherId];
    if (filter && !filter(otherSubRoof)) {
      continue;
    }
    const { roofTopFaces: otherRoofTopFaces, roofVolume: otherRoofVolume } = otherSubRoof;
    const { aabb3: otherAABB3 } = otherRoofVolume;

    if (!aabb3Overlaps(aabb3, otherAABB3)) {
      continue;
    }

    for (let i = 0, l = roofTopFaces.length; i < l; i++) {
      const face = faceOrNullMoveAlongNormal(faceOrNullCopy(roofTopFaces[i].face), offset);
      assertIsNotNull(face);
      const { plane } = face;

      const [nX, nY, nZ] = planeNormal(plane);

      for (let j = 0, lj = otherRoofTopFaces.length; j < lj; j++) {
        const otherFace = faceOrNullMoveAlongNormal(faceOrNullCopy(otherRoofTopFaces[j].face), offset);
        assertIsNotNull(otherFace);
        const { plane: otherPlane } = otherFace;

        if (planeIsParallel(plane, otherPlane)) {
          continue;
        }

        const [otherNX, otherNY, otherNZ] = planeNormal(otherPlane);
        if (vec3AngleBetween(nX, nY, nZ, otherNX, otherNY, otherNZ) >= Math.PI) {
          continue;
        }

        const intersection = intersectFaceFace(face, otherFace, []);
        if (!intersection || intersection.kind !== IntersectionKind.TRANSVERSE) {
          continue;
        }

        const { segments } = intersection;

        for (let k = 0, lk = segments.length; k < lk; k++) {
          const { x0, y0, z0, x1, y1, z1 } = segments[k];
          const valleyRail = polyLine3ByTwoPoints(x0, y0, z0, x1, y1, z1);

          if (polyLine3Length(valleyRail) < VALLEY_RAIL_TOLERANCE) {
            continue;
          }

          const isHorizontal = Math.abs(z1 - z0) < DIST_EPSILON;

          // always poiting up
          if (z1 < z0) {
            polyLine3Reverse(valleyRail);
          }

          polyLine3Move(valleyRail, -(nX + otherNX) * offset, -(nY + otherNY) * offset, -(nZ + otherNZ) * offset);

          (isHorizontal ? horizontalValleyRails : valleyRails).push(valleyRail);
        }
      }
    }
  }
}
